import { Form } from '@ps-design-system/components/Form';
import { InputField } from '@ps-design-system/components/Input';
import {
  SelectContent,
  SelectField,
  SelectItem
} from '@ps-design-system/components/Select';
import React from 'react';
import { injectIntl } from 'react-intl';
import { useCheckoutContext } from '../../CheckoutContextProvider';

export const BillingAddressForm = injectIntl(({ intl }) => {
  const { billingAddressForm, billingCountries, isSubmittingRequest } = useCheckoutContext();

  const [countryOptions, setCountryOptions] = React.useState([]);
  const [zoneOptions, setZoneOptions] = React.useState([]);
  const [selectedCountry, setSelectedCountry] = React.useState(null);

  const selectedCountryCode = billingAddressForm?.watch('country');
  React.useEffect(() => {
    if (!selectedCountryCode || !billingCountries?.length) {
      return;
    }
    const selectedCountry = billingCountries.find(
      (country) => country.code === selectedCountryCode
    );
    const zones = (selectedCountry?.zones || []).filter(
      (zone) => zone.code !== 'BFP'
    );
    setSelectedCountry(selectedCountry);
    setZoneOptions(
      zones.map((zone) => ({
        value: zone.code,
        label: zone.name
      }))
    );
  }, [selectedCountryCode, billingCountries]);

  React.useEffect(() => {
    setCountryOptions(
      (billingCountries || []).map((country) => ({
        value: country.code,
        label: country.name
      }))
    );
  }, [billingCountries]);

  return (
    <Form testId="billing-address-form" {...billingAddressForm}>
      <SelectField
        testId="country-select"
        name="country"
        label={intl.formatMessage({
          id: 'billing_address_form.country_label',
          defaultMessage: 'Country'
        })}
        placeholder={intl.formatMessage({
          id: 'billing_address_form.country_placeholder',
          defaultMessage: 'Select a country'
        })}
        disabled={!countryOptions?.length || isSubmittingRequest}
        fullWidth={true}
        renderContent={() => (
          <SelectContent>
            {(countryOptions || []).map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        )}
        autoComplete="country"
      />

      <InputField
        testId="address1-input"
        name="address1"
        label={
          selectedCountry?.labels?.address1 ||
          intl.formatMessage({
            id: 'billing_address_form.address1_label',
            defaultMessage: 'Address'
          })
        }
        placeholder={
          selectedCountry?.labels?.address1 ||
          intl.formatMessage({
            id: 'billing_address_form.address1_placeholder',
            defaultMessage: 'Address'
          })
        }
        autoComplete="address-line1"
        type="text"
        fullWidth={true}
        disabled={isSubmittingRequest}
      />

      <div className="billing-address-form__city-state-zip">
        <InputField
          testId="city-input"
          style={{ flex: 1 }}
          name="city"
          label={
            selectedCountry?.labels?.city ||
            intl.formatMessage({
              id: 'billing_address_form.city_label',
              defaultMessage: 'City'
            })
          }
          placeholder={
            selectedCountry?.labels?.city ||
            intl.formatMessage({
              id: 'billing_address_form.city_placeholder',
              defaultMessage: 'City'
            })
          }
          autoComplete="address-level2"
          type="text"
          fullWidth={true}
          disabled={isSubmittingRequest}
        />

        {!!zoneOptions?.length && (
          <SelectField
            testId="state-select"
            style={{ flex: 1 }}
            name="state"
            label={
              selectedCountry?.labels?.zone ||
              intl.formatMessage({
                id: 'billing_address_form.state_label',
                defaultMessage: 'State'
              })
            }
            placeholder={
              selectedCountry?.labels?.zone ||
              intl.formatMessage({
                id: 'billing_address_form.state_placeholder',
                defaultMessage: 'State'
              })
            }
            disabled={!zoneOptions?.length || isSubmittingRequest}
            fullWidth={true}
            renderContent={() => (
              <SelectContent>
                {(zoneOptions || []).map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            )}
            autoComplete="address-level1"
          />
        )}

        <InputField
          testId="zip-input"
          style={{ flex: 1 }}
          name="zip"
          label={
            selectedCountry?.labels?.postalCode ||
            intl.formatMessage({
              id: 'billing_address_form.zip_label',
              defaultMessage: 'Zip'
            })
          }
          placeholder={
            selectedCountry?.labels?.postalCode ||
            intl.formatMessage({
              id: 'billing_address_form.zip_placeholder',
              defaultMessage: 'Zip'
            })
          }
          autoComplete="postal-code"
          type="text"
          fullWidth={true}
          disabled={isSubmittingRequest}
        />
      </div>
    </Form>
  );
});
